import { useCallback, useEffect, useState } from 'react'

interface ManualUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectFiles: (files: FileList | null, options: { face: boolean; plate: boolean }) => void
}

export default function ManualUploadModal({ isOpen, onClose, onSelectFiles }: ManualUploadModalProps) {
  const [face, setFace] = useState(false)
  const [plate, setPlate] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null)

  const resetOptions = useCallback(() => {
    setFace(false)
    setPlate(false)
    setSelectedFiles(null)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      resetOptions()
      return
    }

    resetOptions()

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, resetOptions])

  const handleClose = () => {
    resetOptions()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">파일 업로드</h3>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">전처리 옵션</label>
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-900 space-y-3">
              <div className="flex flex-col gap-3 text-sm">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={face}
                    onChange={(e) => setFace(e.target.checked)}
                    className="sr-only"
                  />
                  <span
                    className={`w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${
                      face ? 'bg-black border-black text-white' : 'bg-white border-gray-400 text-transparent'
                    }`}
                  >
                    {face && (
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <div className="leading-tight">
                    <span className="font-medium text-gray-900">얼굴 전처리</span>
                    <p className="text-xs text-gray-500">인물 영역을 자동으로 블러 처리합니다.</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={plate}
                    onChange={(e) => setPlate(e.target.checked)}
                    className="sr-only"
                  />
                  <span
                    className={`w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${
                      plate ? 'bg-black border-black text-white' : 'bg-white border-gray-400 text-transparent'
                    }`}
                  >
                    {plate && (
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <div className="leading-tight">
                    <span className="font-medium text-gray-900">번호판 전처리</span>
                    <p className="text-xs text-gray-500">차량 번호판을 자동으로 블러 처리합니다.</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-800">업로드할 압축 파일</label>
            <input
              type="file"
              accept=".zip,.tar,.tar.gz,.tgz"
              onChange={(e) => setSelectedFiles(e.target.files)}
              className="block w-full text-sm text-gray-900 border border-gray-300 rounded cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
            />
            <p className="text-xs text-gray-500">압축 파일은 한 번에 하나씩만 업로드할 수 있습니다.</p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={() => {
              onSelectFiles(selectedFiles, { face, plate })
              resetOptions()
            }}
            className="px-4 py-2 text-sm rounded text-white bg-gray-800 hover:bg-gray-900 disabled:opacity-60"
            disabled={!selectedFiles || selectedFiles.length === 0}
          >
            업로드 시작
          </button>
        </div>
      </div>
    </div>
  )
}

